import { useState } from 'react';
import { log } from '../../app/logger';
import { updateSetting } from '../../app/settingsB';
import { PreflightChipB } from '../PreflightChipB';
import type { CredentialStateB } from './moreB';

/**
 * B-19 — **the service credential and the pre-flight chip** (design spec §2.4's More row, §2.6).
 *
 * Two deferred things land here together, and they belong together: B-15 reads a stored
 * credential and B-17's Worker requires one, but until this card existed **nothing in B let the
 * user enter it** — Coach Claude could not work at all. The chip beside it is the only reader of
 * B-17's `GET /v1/coach/health`.
 *
 * ── The copy names WHICH secret, because the wrong one is plausible ──────────
 *
 * §5.1: the provider API key is a Worker secret and *"never appears in any client bundle,
 * request, or log"*. A field labelled "API key" on a screen about Coach Claude invites exactly
 * the paste that would put an Anthropic key into a phone's database and into every request it
 * makes. So the label, the hint and the placeholder all say **service credential** — the token
 * for Touchline's own service — and the hint says outright that the Coach Claude key is not
 * entered here and never leaves the service.
 *
 * ── Nothing stored is ever echoed ────────────────────────────────────────────
 *
 * The field is never prefilled. A stored credential is described — *saved, 44 characters* — and
 * `useMore` never puts the value in React state to begin with, so there is no `value` attribute
 * for it to reach and nothing for a screenshot to capture. The length is the one fact a user can
 * act on ("did the whole token paste?") that says nothing about the characters.
 */

const MODULE = 'ui-b/more';

export interface CredentialCardBProps {
  readonly credential: CredentialStateB;
  /** Re-read after a successful write — the imperative re-read convention. */
  readonly onWritten: () => void;
}

export function CredentialCardB({ credential, onWritten }: CredentialCardBProps) {
  const [draft, setDraft] = useState('');
  /** Open whenever there is nothing stored; opened by *Replace* when there is. */
  const [editing, setEditing] = useState(false);
  const [arming, setArming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /**
   * Bumped on every committed credential write, and used as the chip's `key`.
   *
   * A verdict is about the credential that was tested; the moment a different one is stored, the
   * old verdict is a claim about a token this device no longer has. Remounting is the whole
   * mechanism — the chip holds no persisted state to clear.
   */
  const [chipGeneration, setChipGeneration] = useState(0);

  const open = editing || !credential.stored;

  /** One write path: dispatch → sentence on refusal → re-read → clear the field. */
  const write = (value: string, done: () => void): void => {
    setBusy(true);
    setProblem(null);
    void updateSetting('backupCredential', value)
      .then((result) => {
        if (!result.ok) {
          setProblem(result.message);
          return;
        }
        // A credential change invalidates whatever the chip last said about the old one.
        setChipGeneration((n) => n + 1);
        done();
        onWritten();
      })
      .catch((cause: unknown) => {
        log.error(MODULE, 'B §2.6: the service credential could not be saved', {
          message: cause instanceof Error ? cause.message : String(cause),
        });
        setProblem('That could not be saved. The log has the detail.');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const save = (): void => {
    write(draft.trim(), () => {
      setDraft('');
      setEditing(false);
    });
  };

  const remove = (): void => {
    write('', () => {
      setDraft('');
      setEditing(false);
      setArming(false);
    });
  };

  return (
    <section className="more-card" data-testid="more-credential" aria-labelledby="more-cred-h">
      <h2 className="more-card__title" id="more-cred-h">
        Coach Claude
      </h2>
      <p className="more-card__why" data-testid="more-credential-why">
        Coach Claude runs on Touchline’s own service. Paste the <strong>service credential</strong>{' '}
        for it below — this is not an Anthropic API key, and the Anthropic key is never entered
        here or held on this phone: it lives on the service and never leaves it.
      </p>

      <p className="more-cred__state" data-testid="more-credential-state" role="status">
        {credential.stored
          ? `Saved on this device · ${String(credential.length)} characters.`
          : 'No credential saved on this device yet — Coach Claude cannot run without one.'}
      </p>

      {open ? (
        <div className="more-cred__edit">
          <label className="more-field">
            <span className="more-field__label">Service credential</span>
            <input
              className="more-field__input"
              data-testid="more-credential-field"
              type="password"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="Paste the Touchline service credential"
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
            />
          </label>
          <div className="more-actions">
            <button
              type="button"
              className="more-btn more-btn--primary"
              data-testid="more-credential-save"
              disabled={busy || draft.trim() === ''}
              onClick={save}
            >
              Save credential
            </button>
            {credential.stored && (
              <button
                type="button"
                className="more-btn"
                data-testid="more-credential-cancel"
                disabled={busy}
                onClick={() => {
                  setDraft('');
                  setEditing(false);
                  setProblem(null);
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="more-actions">
          <button
            type="button"
            className="more-btn"
            data-testid="more-credential-replace"
            disabled={busy}
            onClick={() => {
              setEditing(true);
              setArming(false);
              setProblem(null);
            }}
          >
            Replace
          </button>
          {arming ? (
            <button
              type="button"
              className="more-btn more-btn--danger"
              data-testid="more-credential-remove-confirm"
              disabled={busy}
              onClick={remove}
            >
              Remove it — confirm
            </button>
          ) : (
            <button
              type="button"
              className="more-btn"
              data-testid="more-credential-remove"
              disabled={busy}
              onClick={() => {
                setArming(true);
                setProblem(null);
              }}
            >
              Remove
            </button>
          )}
        </div>
      )}

      {problem !== null && (
        <p className="more-problem" data-testid="more-credential-problem" role="status">
          {problem}
        </p>
      )}

      {/* §2.6 — the pre-flight. Exercises DNS, CORS, auth and Worker liveness; no model call.
          `surface="more"` is what lets its copy say "above" and mean it: on this card the field —
          or the *Replace* that reopens it — is the element directly overhead (B-24). */}
      <PreflightChipB
        key={chipGeneration}
        surface="more"
        note="The test asks the service whether it is there and whether this credential works. It never asks Coach Claude for suggestions, so it costs nothing and answers in a second or two."
      />
    </section>
  );
}
